// src/pages/CheckoutPage.js
import React, { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { useCart } from "../components/CartContext";
import { canon } from "../lib/seo";
import { API_BASE } from "../lib/api";
import { productImageUrl, productImageAlt, productInStock } from "../context/ProductDataContext";

const LS_KEY = "solarya_cart_v2";

const asNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const fmtFt = (value) => {
  const n = asNumber(value, NaN);
  return Number.isFinite(n) ? `${n.toLocaleString("hu-HU")} Ft` : "—";
};

const toOrderItem = (item) => {
  const id = item?.id || item?.slug;
  return {
    id,
    slug: item?.slug || id,
    name: item?.name || "Termék",
    brand: item?.brand || "",
    price: asNumber(item?.price),
    quantity: Math.max(1, asNumber(item?.quantity, 1)),
    image: productImageUrl(item),
    imageAlt: productImageAlt(item),
    inStock: productInStock(item),
    stock: {
      inStock: productInStock(item),
      quantity: asNumber(item?.stock?.quantity, 0),
      leadTimeDays: asNumber(item?.stock?.leadTimeDays, 0),
    },
  };
};

const sanitizeCartItems = (items) =>
  (Array.isArray(items) ? items : [])
    .map(toOrderItem)
    .filter((item) => item.id);

export default function CheckoutPage() {
  // ---- NAV / URL PARAMS ----
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = useLocation();
  const sp = new URLSearchParams(location.search);
  const sessionId = sp.get("session_id");
  const isOffline = sp.get("offline") === "true";

  // ---- CART ----
  const { cartItems, inc, dec, setQuantity, removeFromCart, total, clearCart } = useCart();
  const orderItems = useMemo(() => sanitizeCartItems(cartItems), [cartItems]);
  const orderTotal = useMemo(
    () => orderItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
    [orderItems]
  );
  const displayTotal = Number.isFinite(Number(total)) ? Number(total) : orderTotal;

  // ---- STEPS: 1 Kosár → 2 Szállítás & Fizetés → 3 Összegzés (siker) ----
  const [step, setStep] = useState(1); // alapértelmezés
  useEffect(() => {
    // Ha Stripe visszatért session_id-vel vagy offline=true-vel, lépjünk az Összegzésre
    if (sessionId || isOffline) setStep(3);
  }, [sessionId, isOffline]);

  // ---- FORM (Szállítás & Fizetés) ----
  const [delivery, setDelivery] = useState("personal"); // "personal" | "address"
  const [payment, setPayment] = useState("COD");        // "COD" | "Online" | "OnSite"
  const [addr, setAddr] = useState({
    country: "Magyarország",
    postalCode: "",
    city: "",
    street: "",
    houseNumber: "",
    phone: "",
    email: "",
    isCompany: false,
    companyName: "",
    taxNumber: "",
  });
  const [errors, setErrors] = useState({});

  // Stripe fail eset → maradjon a 2. lépésen
  useEffect(() => {
    const failed = sp.get("payment") === "failed";
    if (failed) setStep(2);
  }, [location.search]); // sp derived

  const validateStep2 = () => {
    const e = {};
    if (delivery === "address") {
      if (!addr.country) e.country = "Kötelező mező";
      if (!addr.postalCode) e.postalCode = "Kötelező mező";
      if (!addr.city) e.city = "Kötelező mező";
      if (!addr.street) e.street = "Kötelező mező";
      if (!addr.houseNumber) e.houseNumber = "Kötelező mező";
    }
    if (!addr.phone || addr.phone.replace(/\D/g, "").length < 7) e.phone = "Érvénytelen telefonszám";
    if (!addr.email || !/\S+@\S+\.\S+/.test(addr.email)) e.email = "Érvénytelen e-mail";
    if (addr.isCompany) {
      if (!addr.companyName) e.companyName = "Kötelező cégnév";
      if (!addr.taxNumber) e.taxNumber = "Kötelező adószám";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ---- LÉPÉS 2 → TOVÁBB ----
  const proceedFromStep2 = async () => {
    if (!validateStep2()) return;

    // Mentjük, hogy az összegzés (és email) rész lássa
    Cookies.set("solarya_form", JSON.stringify({ delivery, payment, ...addr }), { expires: 1 });

    if (payment === "Online") {
      // Stripe: a backend success_url-je legyen:  http://localhost:3000/megrendeles?session_id={CHECKOUT_SESSION_ID}
      try {
        const res = await fetch(`${API_BASE}/api/create-checkout-session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ cartItems: orderItems, form: { delivery, payment, ...addr } }),
        });
        const data = await res.json();
        if (data?.url) {
          window.location.href = data.url;
          return;
        }
        alert("Hiba történt a fizetés indításakor.");
      } catch (err) {
        console.error("Stripe hiba:", err);
        alert("Hiba történt a fizetés indításakor.");
      }
    } else {
      // Offline ágak → ugyanitt, a checkouton lépünk az összegzésre
      // Tisztább, ha URL-be is tesszük, így refresh esetén is stabil: ?offline=true
      const qp = new URLSearchParams(location.search);
      qp.set("offline", "true");
      navigate({ pathname: location.pathname, search: qp.toString() }, { replace: true });
      setStep(3);
    }
  };

  const canContinueFromStep1 = orderItems.length > 0;

  // ------------- ÖSSZEGZÉS (3. LÉPÉS): e-mail küldés + ürítés -------------
  const [emailSent, setEmailSent] = useState(false);
  const [emailFailed, setEmailFailed] = useState(false);
  const emailAlreadySentRef = useRef(false);
  const [summaryItems, setSummaryItems] = useState([]);
  const [summaryForm, setSummaryForm] = useState(null);

  // Összegzés adatainak beolvasása és e-mail küldés (Stripe / offline is)
  useEffect(() => {
    if (step !== 3) return;

    const shouldSendEmail = !!sessionId || !!isOffline;
    if (!shouldSendEmail || emailAlreadySentRef.current) {
      loadSummaryData();
      return;
    }

    emailAlreadySentRef.current = true;
    loadSummaryData(true);
    sendEmailAsync().catch((err) => console.error("Email küldés hiba:", err));

    // 30 mp timeout: ha addig nem sikerül az e-mail küldés, hibát jelzünk
    const timer = setTimeout(() => {
      if (!emailSent) {
        console.warn("Rendelés visszaigazoló e-mail nem érkezett 30 mp-en belül.");
        setEmailFailed(true);
      }
    }, 30000);

    return () => clearTimeout(timer);
  }, [step, sessionId, isOffline]);


  function loadSummaryData(prefillFromStorage = false) {
    try {
      // Elsődlegesen cookie → ha üres, fallback LS
      const cartFromCookie = JSON.parse(Cookies.get("cartItems") || "[]");
      let cart = cartFromCookie;
      if ((!cart || cart.length === 0) && prefillFromStorage) {
        const lsRaw = localStorage.getItem(LS_KEY);
        if (lsRaw) {
          const parsed = JSON.parse(lsRaw);
          if (Array.isArray(parsed)) cart = parsed;
        }
      }
      setSummaryItems(sanitizeCartItems(cart));

      const formData = JSON.parse(Cookies.get("solarya_form") || "null");
      if (formData) setSummaryForm(formData);
    } catch (err) {
      console.error("Adat betöltési hiba:", err);
    }
  }

  async function sendEmailAsync() {
    try {
      // cookie-ból olvasunk, de fallbackeljünk LS-re ha üres
      let cart = JSON.parse(Cookies.get("cartItems") || "[]");
      const formData = JSON.parse(Cookies.get("solarya_form") || "null");
      if (!cart || cart.length === 0) {
        const lsRaw = localStorage.getItem(LS_KEY);
        if (lsRaw) {
          const parsed = JSON.parse(lsRaw);
          if (Array.isArray(parsed)) cart = parsed;
        }
      }

      const res = await fetch(`${API_BASE}/api/send-confirmation-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ form: formData, cartItems: sanitizeCartItems(cart) }),
      });

      if (res.ok) {
        setEmailSent(true);
        // Takarítás: cookie + LS törlés, hogy a kosár tényleg ürüljön
        Cookies.remove("solarya_form");
        Cookies.remove("cartItems");
        try { localStorage.removeItem(LS_KEY); } catch { }
        // UI kosár ürítése
        clearCart();
      } else {
        console.error("Email küldés sikertelen.");
      }
    } catch (err) {
      console.error("Email küldés hiba:", err);
    }
  }

  const summaryTotal = useMemo(() => {
    return summaryItems.reduce(
      (sum, item) => sum + asNumber(item?.price) * Math.max(1, asNumber(item?.quantity, 1)),
      0
    );
  }, [summaryItems]);

  /* ========================= R E N D E R ========================= */

  // Lépés címkék
  const stepLabel = step === 1 ? "Kosár" : step === 2 ? "Szállítás & Fizetés" : "Összegzés";

  return (
    <>
      {/* SEO – Checkout: noindex */}
      <title>Megrendelés – Solarya</title>
      <meta name="robots" content="noindex, nofollow" />
      <link rel="canonical" href={canon(pathname)} />

      <div className="min-h-screen bg-gradient-to-b from-white to-slate-50">
        {/* ===== HERO – egységes, lépésjelző nélkül ===== */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[#0c1a26] to-[#173047]" />
          <div className="absolute inset-0 opacity-25">
            <div
              className="w-full h-full"
              style={{
                backgroundImage: `url(${"/img/t5.webp"})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
          </div>

          <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-20 md:pt-20 md:pb-28 text-white">
            <div className="text-sm uppercase tracking-wide text-white/80">Checkout</div>
            <h1 className="text-3xl md:text-5xl font-bold mt-1 drop-shadow-sm">Megrendelés</h1>
            <p className="text-white/90 text-lg md:text-xl max-w-3xl mt-2">
              Válaszd ki a szállítási és fizetési módot, majd erősítsd meg a rendelést.
            </p>
          </div>
        </section>

        {/* ===== LÉPÉSJELZŐ – a hero alatt, kontrasztos sáv ===== */}
        <section className="bg-white/60 backdrop-blur border-b">
          <div className="max-w-7xl mx-auto px-6">
            <div className="py-3 md:py-4 flex items-center justify-center">
              <div className="w-full max-w-3xl flex items-center justify-between bg-white/70 backdrop-blur-sm border border-gray-200 rounded-full px-5 py-2 text-sm md:text-base">
                <div className={`flex items-center gap-2 ${step === 1 ? "text-gray-900" : "text-gray-600"}`}>
                  <span className={`px-2 py-0.5 rounded-full text-xs md:text-sm ${step === 1 ? "bg-blue-600 text-white" : "bg-gray-200"}`}>
                    🛒
                  </span>
                  <span className="font-medium">1. Kosár</span>
                </div>
                <span className="text-gray-400">→</span>
                <div className={`flex items-center gap-2 ${step === 2 ? "text-gray-900" : "text-gray-600"}`}>
                  <span className={`px-2 py-0.5 rounded-full text-xs md:text-sm ${step === 2 ? "bg-blue-600 text-white" : "bg-gray-200"}`}>
                    🚚
                  </span>
                  <span className="font-medium">2. Szállítás & Fizetés</span>
                </div>
                <span className="text-gray-400">→</span>
                <div className={`flex items-center gap-2 ${step === 3 ? "text-gray-900" : "text-gray-600"}`}>
                  <span className={`px-2 py-0.5 rounded-full text-xs md:text-sm ${step === 3 ? "bg-blue-600 text-white" : "bg-gray-200"}`}>
                    ✅
                  </span>
                  <span className="font-medium">3. Összegzés</span>
                </div>
              </div>
            </div>
          </div>
        </section>


        {/* ===== TARTALOM ===== */}
        <div className="max-w-7xl mx-auto px-6 py-10">
          {/* Info bar hibára */}
          {sp.get("payment") === "failed" && (
            <div className="mb-6 rounded-xl border border-rose-200 bg-rose-50 text-rose-800 px-4 py-3">
              A fizetés nem sikerült. Próbáld újra.
            </div>
          )}

          {/* 1. lépés – Kosár */}
          {step === 1 && (
            <section className="grid md:grid-cols-[1fr_22rem] gap-6">
              <div className="bg-white/90 backdrop-blur rounded-2xl border p-4 md:p-5">
                <h2 className="text-2xl font-bold mb-4">Kosár</h2>
                {orderItems.length === 0 ? (
                  <p className="text-gray-700">A kosár üres.</p>
                ) : (
                  <ul className="space-y-3">
                    {orderItems.map((it) => (
                      <li key={it.id} className="flex gap-3 border rounded-xl p-3">
                        <img src={productImageUrl(it)} alt={productImageAlt(it)} className="w-20 h-20 rounded object-contain bg-gray-50 border" />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium line-clamp-2">{it.name}</div>
                          <div className="text-sm text-gray-600">{fmtFt(it.price)}</div>
                          <div className="mt-2 flex items-center gap-2">
                            <button className="w-8 h-8 border rounded" onClick={() => dec(it.id || it.slug)} aria-label="−">−</button>
                            <input
                              className="w-16 h-8 border rounded text-center"
                              type="number" min={1} max={99} value={it.quantity}
                              onChange={(e) => setQuantity(it.id || it.slug, e.target.value)}
                            />
                            <button className="w-8 h-8 border rounded" onClick={() => inc(it.id || it.slug)} aria-label="+">+</button>
                            <button className="ml-auto text-rose-600 text-sm" onClick={() => removeFromCart(it.id || it.slug)}>Eltávolítás</button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-5 flex items-center justify-between rounded-xl bg-gray-50 border px-4 py-3">
                  <div className="text-gray-700">Részösszeg</div>
                  <div className="font-semibold">{fmtFt(displayTotal)}</div>
                </div>
                <div className="mt-4 flex justify-between">
                  <button className="border px-5 py-2 rounded hover:bg-gray-50" onClick={() => navigate("/kategoriak")}>
                    Vissza a termékekhez
                  </button>
                  <button
                    disabled={!canContinueFromStep1}
                    onClick={() => setStep(2)}
                    className="px-6 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                  >
                    Folytatás
                  </button>
                </div>
              </div>

              <aside className="bg-gray-50/90 backdrop-blur rounded-2xl border p-4 md:p-5 h-fit md:sticky md:top-24">
                <div className="flex items-center justify-between text-lg font-semibold">
                  <span>Összesen</span>
                  <span>{fmtFt(displayTotal)}</span>
                </div>
                <p className="text-xs text-gray-600 mt-1">Az árak bruttó árak, forintban.</p>
                <button
                  disabled={orderItems.length === 0}
                  onClick={() => setStep(2)}
                  className="mt-4 w-full h-11 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  Tovább
                </button>
                <button onClick={() => clearCart()} className="mt-2 w-full h-10 rounded-lg border hover:bg-white">
                  Kosár ürítése
                </button>
              </aside>
            </section>
          )}

          {/* 2. lépés – Szállítás & Fizetés */}
          {step === 2 && (
            <section className="grid md:grid-cols-[1fr_22rem] gap-6">
              <div className="bg-white/90 backdrop-blur rounded-2xl border p-4 md:p-5 space-y-6">
                <h2 className="text-2xl font-bold">Szállítás & Fizetés</h2>

                {/* Szállítási mód */}
                <div>
                  <h3 className="font-semibold mb-2">Szállítási mód</h3>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input type="radio" name="delivery" value="personal"
                        checked={delivery === "personal"} onChange={() => setDelivery("personal")} />
                      Személyes átvétel (üzletünkben)
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="radio" name="delivery" value="address"
                        checked={delivery === "address"} onChange={() => setDelivery("address")} />
                      Szállítás címre
                    </label>
                  </div>

                  {delivery === "address" && (
                    <div className="mt-3 grid sm:grid-cols-2 gap-3">
                      <Field v={addr.country} onC={(v) => setAddr({ ...addr, country: v })} p="Ország" e={errors.country} />
                      <Field v={addr.postalCode} onC={(v) => setAddr({ ...addr, postalCode: v })} p="Irányítószám" e={errors.postalCode} />
                      <Field v={addr.city} onC={(v) => setAddr({ ...addr, city: v })} p="Város" e={errors.city} />
                      <div className="grid grid-cols-[1fr_auto] gap-2">
                        <Field v={addr.street} onC={(v) => setAddr({ ...addr, street: v })} p="Utca" e={errors.street} />
                        <Field v={addr.houseNumber} onC={(v) => setAddr({ ...addr, houseNumber: v })} p="Hsz." e={errors.houseNumber} w="w-24" />
                      </div>
                    </div>
                  )}
                </div>

                {/* Kapcsolat / számlázás */}
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field v={addr.phone} onC={(v) => setAddr({ ...addr, phone: v })} p="Telefon" e={errors.phone} />
                  <Field v={addr.email} onC={(v) => setAddr({ ...addr, email: v })} p="E-mail" e={errors.email} />
                  <label className="col-span-2 flex items-center gap-2 mt-1">
                    <input type="checkbox" checked={addr.isCompany} onChange={(e) => setAddr({ ...addr, isCompany: e.target.checked })} />
                    Cég nevére kérem a számlát
                  </label>
                  {addr.isCompany && (
                    <>
                      <Field v={addr.companyName} onC={(v) => setAddr({ ...addr, companyName: v })} p="Cégnév" e={errors.companyName} />
                      <Field v={addr.taxNumber} onC={(v) => setAddr({ ...addr, taxNumber: v })} p="Adószám" e={errors.taxNumber} />
                    </>
                  )}
                </div>

                {/* Fizetés */}
                <div>
                  <h3 className="font-semibold mt-2 mb-2">Fizetési mód</h3>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input type="radio" name="payment" value="COD" checked={payment === "COD"} onChange={() => setPayment("COD")} />
                      Utánvét
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="radio" name="payment" value="Online" checked={payment === "Online"} onChange={() => setPayment("Online")} />
                      Online bankkártya
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="radio" name="payment" value="OnSite" checked={payment === "OnSite"} onChange={() => setPayment("OnSite")} />
                      Helyszínen
                    </label>
                  </div>
                </div>

                <div className="flex justify-between">
                  <button className="border px-5 py-2 rounded hover:bg-gray-50" onClick={() => setStep(1)}>
                    Vissza
                  </button>
                  <button
                    className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
                    onClick={proceedFromStep2}
                  >
                    {payment === "Online"
                      ? "Fizetés"
                      : (payment === "COD" || payment === "OnSite")
                        ? "Rendelés leadása"
                        : "Tovább"}
                  </button>

                </div>
              </div>

              <aside className="bg-gray-50/90 backdrop-blur rounded-2xl border p-4 md:p-5 h-fit md:sticky md:top-24">
                <h3 className="font-semibold mb-2">Rövid összegzés</h3>
                <ul className="space-y-2 max-h-72 overflow-auto pr-1">
                  {orderItems.map((it) => (
                    <li key={it.id} className="flex items-center gap-2 text-sm">
                      <img src={productImageUrl(it)} alt="" className="w-10 h-10 rounded object-contain bg-white border" />
                      <div className="flex-1">
                        <div className="line-clamp-1">{it.name}</div>
                        <div className="text-gray-600">{it.quantity} × {fmtFt(it.price)}</div>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="mt-3 flex items-center justify-between font-semibold">
                  <span>Összesen</span><span>{fmtFt(displayTotal)}</span>
                </div>
              </aside>
            </section>
          )}

          {/* 3. lépés – Összegzés (OrderSuccessPage integrálva) */}
          {step === 3 && (
            <section className="max-w-3xl mx-auto">
              <div className="p-6 md:p-8 text-center bg-white/90 backdrop-blur rounded-2xl border">
                {/* Állapotjelzés: ha még nem jött vissza az e-mail */}
                {!emailSent && !emailFailed && (
                  <>
                    <div className="text-4xl mb-2 animate-pulse">⏳</div>
                    <h1 className="text-2xl font-semibold mb-2 text-gray-800">Rendelés feldolgozása...</h1>
                    <p className="text-gray-600">
                      Kérlek, várj néhány másodpercet, amíg feldolgozzuk és elküldjük a visszaigazoló e-mailt.
                    </p>
                  </>
                )}

                {/* SIKERES RENDELÉS */}
                {emailSent && (
                  <>
                    <div className="text-4xl mb-2">✅</div>
                    <h1 className="text-3xl font-bold mb-2 text-green-600">Rendelés sikeres!</h1>
                    <p className="text-gray-700">
                      Köszönjük a rendelést. A visszaigazoló e-mailt elküldtük a megadott címre.
                    </p>
                  </>
                )}

                {/* HIBÁS RENDELÉS / TIMEOUT */}
                {emailFailed && (
                  <>
                    <div className="text-4xl mb-2">⚠️</div>
                    <h1 className="text-2xl font-bold mb-2 text-rose-600">Hiba történt</h1>
                    <p className="text-gray-700">
                      A rendelés feldolgozása nem fejeződött be 30 másodpercen belül.
                      Kérjük, próbáld újra később, vagy vedd fel velünk a kapcsolatot.
                    </p>
                    <button
                      className="mt-5 px-5 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                      onClick={sendEmailAsync}
                    >
                      Újrapróbálás
                    </button>
                  </>
                )}

                {/* Megrendelt tételek – csak ha már betöltöttük az adatokat */}
                {summaryItems.length > 0 && (
                  <div className="text-left mt-6 bg-gray-50 rounded-xl border">
                    <div className="px-4 py-3 border-b font-semibold">Megrendelt tételek</div>
                    <ul className="px-4 py-3 space-y-2">
                      {summaryItems.map((item) => (
                        <li key={item.id} className="flex justify-between">
                          <div className="pr-3">
                            {item.name} × {Number(item.quantity) || 1}
                          </div>
                          <div className="tabular-nums">
                            {fmtFt((Number(item.price) || 0) * (Number(item.quantity) || 1))}
                          </div>
                        </li>
                      ))}
                    </ul>
                    <div className="px-4 py-3 text-right border-t font-bold">
                      Összesen: {fmtFt(summaryTotal)}
                    </div>
                  </div>
                )}

                {/* Vissza gomb – csak siker / hiba után */}
                {(emailSent || emailFailed) && (
                  <div className="mt-6">
                    <button
                      className="inline-flex items-center justify-center rounded-xl bg-[#0c1a26] text-white px-5 py-3 hover:bg-black/90"
                      onClick={() => navigate("/kategoriak")}
                    >
                      Vissza a termékekhez
                    </button>
                  </div>
                )}
              </div>

            </section>
          )}
        </div>
      </div>
    </>
  );
}

/* ---------- Kisegítő komponens  ---------- */
function Field({ v, onC, p, e, w = "w-full" }) {
  return (
    <div className={w}>
      <input
        className={`w-full border rounded px-3 py-2 ${e ? "border-rose-400" : ""}`}
        value={v}
        onChange={(e) => onC(e.target.value)}
        placeholder={p}
      />
      {e && <p className="text-xs text-rose-600 mt-1">{e}</p>}
    </div>
  );
}
